'use client';

import { getCachedSession } from '@/features/auth/utils/sessionCache';
import { ChatState } from '../types/chat';
import { ChatService } from './chatService';
import { chatSocketService } from './chatSocketService';

export class ChatEventManager {
  static async setupSocketListeners(
    get: () => ChatState,
    set: (fn: (state: ChatState) => Partial<ChatState>) => void
  ): Promise<void> {
    const session = await getCachedSession();
    const currentUserId = session?.user?.id ? parseInt(session.user.id) : 0;

    // Chat list updates
    await chatSocketService.onChatsList(async (data) => {
      const { contacts, messages, chatHistory } = await ChatService.convertContactsFromServer(data.chats);

      set((state) => ({
        contacts,
        messages: { ...state.messages, ...messages },
        chatHistory: { ...state.chatHistory, ...chatHistory },
      }));
    });

    // New message received
    await chatSocketService.onNewMessage((data) => {
      const message = ChatService.convertSocketMessageToLocal(data, currentUserId);
      const chatRoomId = data.chatRoomId;

      // Replace temporary message if it's user's own message
      if (message.isOwn) {
        const state = get();
        const chatMessages = state.messages[chatRoomId] || [];
        const pendingMessage = ChatService.findPendingMessage(chatMessages, message);

        if (pendingMessage?.tempId) {
          get().replaceTemporaryMessage(chatRoomId, pendingMessage.tempId, message);
          return;
        }
      }

      // Add new message
      get().addMessageToChat(chatRoomId, message);

      // Update unread count if not active chat
      if (ChatService.shouldNotifyUser(get().activeContactId, chatRoomId)) {
        set((state) => ({
          contacts: state.contacts.map((contact) =>
            contact.id === chatRoomId
              ? {
                ...contact,
                lastMessage: message.message,
                lastMessageTime: message.timestamp,
                unreadCount: (contact.unreadCount || 0) + 1,
              }
              : contact
          ),
        }));
      }
    });

    // Chat history received
    await chatSocketService.onChatHistory((data) => {
      const messages = data.messages.map((msg) =>
        ChatService.convertSocketMessageToLocal(msg, currentUserId)
      );
      const state = get();
      const existingMessages = state.messages[data.chatRoomId] || [];

      if (existingMessages.length > 0) {
        // Infinite scroll - prepend messages
        const filteredMessages = ChatService.filterUniqueMessages(messages, existingMessages);
        if (filteredMessages.length > 0) {
          set((state) => ({
            messages: {
              ...state.messages,
              [data.chatRoomId]: [...filteredMessages, ...existingMessages],
            },
          }));
        }
      } else {
        // Initial load - replace messages
        set((state) => ({
          messages: {
            ...state.messages,
            [data.chatRoomId]: messages,
          },
        }));
      }

      // Update pagination metadata
      set((state) => ({
        chatHistory: {
          ...state.chatHistory,
          [data.chatRoomId]: {
            ...state.chatHistory[data.chatRoomId],
            hasMore: data.hasMore,
            nextCursor: data.nextCursor,
            total: data.total,
            isLoadingMore: false,
          },
        },
      }));
    });

    // Direct chat started
    await chatSocketService.onChatStarted((data) => {
      get().loadContacts();
      get().openChat(data.chatRoom.id);
    });

    // New chat created by other user
    await chatSocketService.onNewChatCreated((data) => {
      const state = get();
      const { chatRoom } = data;
      const otherUser = chatRoom.otherParticipant;

      // 새로운 채팅방을 contacts에 추가
      const newContact = {
        id: chatRoom.id,
        name: otherUser.companyName || `${otherUser.firstName} ${otherUser.lastName}`,
        username: otherUser.username,
        unreadCount: 0,
      };

      set((state) => ({
        contacts: [newContact, ...state.contacts],
      }));

      if (!state.isOverlayOpen) {
        get().openChat(chatRoom.id);
        set(() => ({
          isOverlayOpen: true,
          activeContactId: chatRoom.id
        }));
      }
    });

    // Chat list updates
    await chatSocketService.onChatListUpdated((data) => {
      const { chatRoomId, lastMessage, lastMessageAt } = data;
      set((state) => ({
        contacts: state.contacts.map((contact) =>
          contact.id === chatRoomId
            ? {
              ...contact,
              lastMessage: lastMessage.content,
              lastMessageTime: new Date(lastMessageAt),
            }
            : contact
        ),
      }));
    });

    // Messages marked as read
    await chatSocketService.onMessagesMarkedAsRead((data) => {
      const { chatRoomId, userId, messageIds } = data;
      set((state) => ({
        messages: {
          ...state.messages,
          [chatRoomId]: (state.messages[chatRoomId] || []).map((msg) =>
            (!messageIds || messageIds.includes(msg.id)) && msg.senderId === userId
              ? { ...msg, isRead: true }
              : msg
          ),
        },
      }));
    });

    // Unread count updates
    await chatSocketService.onUnreadCount((data) => {
      const { chatRoomId, count } = data;
      set((state) => ({
        unreadCounts: {
          ...state.unreadCounts,
          [chatRoomId]: count,
        },
        contacts: state.contacts.map((contact) =>
          contact.id === chatRoomId ? { ...contact, unreadCount: count } : contact
        ),
      }));
    });

    // Error handling
    await chatSocketService.onError(() => {
    });
  }
}